/**
 * Withhold Client Contact: My Tasks' two handoff groups.
 *
 * - "Handoffs to Coordinate" (seller, `share_contact_info`): above Urgent,
 *   because a pending handoff blocks the buyer.
 * - "Awaiting Contact Info" (buyer, `awaiting_contact_info`): below Next Up,
 *   because it is informational -- the buyer has nothing to do yet.
 *
 * Both arrive in the server's Urgent or Next Up section like any other task.
 * My Tasks lifts them out of both into their own group.
 *
 * Each group is built from the tasks loaded so far: the needs-action endpoint
 * has no per-action-type total, so a grouped task on a page not yet fetched is
 * not counted until that page loads. The prefetch window (30 urgent / 20 next
 * up) makes this exact in practice.
 */

import { CASE_PURCHASE_STATUS } from '@/constants'
import type { TaskEntry } from '../types'

/** Whether a task belongs to the "Handoffs to Coordinate" group. */
export const isHandoffTask = (task: TaskEntry): boolean =>
  task.urgency_metadata?.action_type === 'share_contact_info'

/** Whether a task belongs to the buyer's "Awaiting Contact Info" group. */
export const isAwaitingContactTask = (task: TaskEntry): boolean =>
  task.urgency_metadata?.action_type === 'awaiting_contact_info'

/** Whether a task leaves Urgent / Next Up for one of the handoff groups. */
export const isGroupedTask = (task: TaskEntry): boolean =>
  isHandoffTask(task) || isAwaitingContactTask(task)

/** Number of loaded grouped tasks in a section's items. */
export const countGroupedTasks = (items: TaskEntry[]): number =>
  items.filter(isGroupedTask).length

/**
 * A section's state without its grouped tasks, so TaskSection's reveal /
 * load-more logic compares `displayCount` against the cards it can actually
 * show.
 */
export const withoutGroupedTasks = <T extends { items: TaskEntry[] }>(
  state: T
): T => ({ ...state, items: state.items.filter(task => !isGroupedTask(task)) })

/**
 * The cards a section reveals: its first `displayCount` ungrouped tasks.
 * Filtering before slicing keeps a full window when handoffs sort first
 * (share_contact_info has a high urgency priority), so the desktop
 * scroll-to-reveal container still has something to scroll.
 */
export const revealedSectionTasks = (state: {
  items: TaskEntry[]
  displayCount: number
}): TaskEntry[] =>
  state.items.filter(task => !isGroupedTask(task)).slice(0, state.displayCount)

/**
 * The purchase whose client contact info is withheld: the match, which may
 * already be Signed -- the buyer can sign before the share.
 */
export const getWithheldCasePurchase = (task: TaskEntry) =>
  task.case_purchase?.find(cp => cp.status === CASE_PURCHASE_STATUS.Accept) ??
  task.case_purchase?.find(cp => cp.status === CASE_PURCHASE_STATUS.Signed)
